import { logger } from './logger';

/**
 * Standard user role types
 */
export type UserRole = 'superadmin' | 'admin' | 'user';

/**
 * Role information with utility flags
 */
export interface RoleInfo {
  role: UserRole;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  isEmployee: boolean;
  displayName: string;
}

/**
 * Normalize role from various API formats
 *
 * This utility handles inconsistencies in role field names and values
 * across different API endpoints.
 *
 * @param user - User object from API or state
 * @returns Normalized role type or null if invalid/missing
 *
 * @example
 * normalizeRole({ role: 'superadmin' }) // 'superadmin'
 * normalizeRole({ role: 'super_admin' }) // 'superadmin'
 * normalizeRole({ roleType: 'ADMIN' }) // 'admin'
 * normalizeRole({ userRole: 'employee' }) // 'user'
 */
export const normalizeRole = (user: any): UserRole | null => {
  if (!user) return null;

  // Try different field names that might contain the role
  const roleValue = user.role || user.roleType || user.userRole || user.type;

  if (!roleValue) return null;

  // Normalize to lowercase and trim whitespace
  const normalized = String(roleValue).toLowerCase().trim();

  // Map variations to standard values
  if (normalized.includes('super')) {
    return 'superadmin';
  }

  if (normalized.includes('admin')) {
    return 'admin';
  }

  if (
    normalized.includes('user') ||
    normalized.includes('employee') ||
    normalized.includes('customer')
  ) {
    return 'user';
  }

  // Unknown role type
  logger.warn('Unknown role type:', roleValue);
  return null;
};

/**
 * Get comprehensive role information with utility flags
 *
 * @param user - User object from API or state
 * @returns RoleInfo object with role and boolean flags, or null if invalid
 *
 * @example
 * const roleInfo = getRoleInfo(user);
 * if (roleInfo?.isSuperAdmin) { // render super admin UI }
 */
export const getRoleInfo = (user: any): RoleInfo | null => {
  const role = normalizeRole(user);

  if (!role) return null;

  return {
    role,
    isSuperAdmin: role === 'superadmin',
    isOrgAdmin: role === 'admin',
    isEmployee: role === 'user',
    displayName: {
      superadmin: 'Super Administrator',
      admin: 'Organization Administrator',
      user: 'Employee',
    }[role],
  };
};

/**
 * Check if user has specific role
 *
 * @param user - User object
 * @param role - Role to check against
 * @returns true if user has the specified role
 *
 * @example
 * if (hasRole(user, 'superadmin')) { // allow access }
 */
export const hasRole = (user: any, role: UserRole): boolean => {
  return normalizeRole(user) === role;
};

/**
 * Check if user has any of the specified roles
 *
 * @param user - User object
 * @param roles - Array of roles to check against
 * @returns true if user has any of the specified roles
 *
 * @example
 * if (hasAnyRole(user, ['superadmin', 'admin'])) { // allow admin access }
 */
export const hasAnyRole = (user: any, roles: UserRole[]): boolean => {
  const userRole = normalizeRole(user);
  return userRole ? roles.includes(userRole) : false;
};

/**
 * Check if user has all of the specified roles
 * This is primarily useful for validation logic
 *
 * @param user - User object
 * @param roles - Array of roles to check against
 * @returns true if user has all of the specified roles (typically not used as users have single role)
 */
export const hasAllRoles = (user: any, roles: UserRole[]): boolean => {
  const userRole = normalizeRole(user);
  if (!userRole) return false;
  // In this system, users have a single role, so this only returns true if roles array contains only that role
  return roles.length === 1 && roles[0] === userRole;
};

/**
 * Get role-specific dashboard path
 *
 * @param user - User object
 * @returns Dashboard path for the user's role
 *
 * @example
 * const dashboardPath = getRoleDashboardPath(user);
 * navigate(dashboardPath);
 */
export const getRoleDashboardPath = (user: any): string => {
  const role = normalizeRole(user);

  switch (role) {
    case 'superadmin':
      return '/dashboard';
    case 'admin':
      return '/admin';
    case 'user':
      return '/recognition-tasks';
    default:
      return '/';
  }
};

/**
 * Validate role value
 * Useful for API response validation
 *
 * @param role - Role string to validate
 * @returns true if role is valid
 */
export const isValidRole = (role: string): role is UserRole => {
  return ['superadmin', 'admin', 'user'].includes(role);
};
